/**
 * Слова руками — главный вход подборки: сотрудник кладёт в свою папку то, что
 * хочет собирать, не дожидаясь фраз Wordstat (у подборки их может и не быть).
 * У общей тематики та же форма заводит типы. Живёт в окне «+ Слово».
 *
 * Слов можно сразу несколько — по одному на строку: список обычно приносят из
 * заметок, и вбивать его по одному — пятнадцать циклов. Запросы идут по
 * очереди, не пачкой: сервис добавляет слово идемпотентно (повтор вернёт
 * прежнее и снова включит отброшенное), так что упавшую строку безопасно
 * отправить ещё раз — она и остаётся в поле. Название — только у одиночного
 * слова: у пачки оно бессмысленно, по умолчанию сервис берёт фразу.
 */
import { useMutation } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useId, useState, type FormEvent } from "react";

import { DialogFooter } from "@/components/trends/Dialog";
import { btn, btnPrimary, ErrorLine, errorText, input, label } from "@/components/trends/ui";
import { plural } from "@/lib/library";
import { normPhrase, post, suggestTypeName, type Topic } from "@/lib/trends";

/** Строки поля → фразы: без пустых и без повторов (сравниваем как сервис). */
const parseLines = (text: string): string[] => [
  ...new Map(
    text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => [normPhrase(l), l] as const),
  ).values(),
];

export function AddWordForm({
  topic,
  onAdded,
  onCancel,
}: {
  topic: Topic;
  /** `complete` — вошли все строки, окно можно закрывать. */
  onAdded: (names: string[], complete: boolean) => void;
  onCancel: () => void;
}) {
  const listId = useId();
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [group, setGroup] = useState("");
  const [failed, setFailed] = useState<string[]>([]);
  const personal = topic.owner_user_id !== 0;
  const groups = [...new Set(topic.types.map((t) => t.grp))].sort((a, b) => a.localeCompare(b, "ru"));
  const lines = parseLines(text);
  const single = lines.length === 1;
  const noun = (n: number) => (personal ? plural(n, "слово", "слова", "слов") : plural(n, "тип", "типа", "типов"));

  const add = useMutation({
    mutationFn: async () => {
      const added: string[] = [];
      const bad: { line: string; error: string }[] = [];
      for (const line of lines) {
        // Без названия у общей тематики — фраза минус затравка («кружевное»), как у фраз Wordstat.
        const title = (single && name.trim()) || (topic.phrase ? suggestTypeName(line, topic.phrase) : "");
        try {
          await post<{ id: number }>(`/api/trends/${topic.id}/types`, {
            phrase: line,
            name: title,
            group: group.trim(),
          });
          added.push(title || line);
        } catch (error) {
          bad.push({ line, error: errorText(error) });
        }
      }
      return { added, bad };
    },
    onSuccess: ({ added, bad }) => {
      setFailed(bad.map((b) => `${b.line}: ${b.error}`));
      if (added.length) onAdded(added, bad.length === 0);
      // Не вошедшие строки остаются в поле — поправить и отправить ещё раз.
      setText(bad.map((b) => b.line).join("\n"));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (lines.length && !add.isPending) add.mutate();
  };

  return (
    <form onSubmit={submit} data-annot="добавить слово">
      <div className="grid gap-3 px-4 py-3">
        <label className="grid gap-1">
          <span className={label}>{personal ? "слова или фразы" : "фразы типов"} — по одной на строку</span>
          <textarea
            className={`${input} h-auto min-h-[120px] w-full resize-y py-1.5 leading-[1.45]`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit(e);
            }}
            placeholder={"платье с открытой спиной\nплатье рыбка\nатласное платье"}
            autoFocus
          />
        </label>
        <label className="grid gap-1">
          <span className={label}>группа (необязательно{lines.length > 1 ? ", одна на все" : ""})</span>
          <input
            className={`${input} w-full`}
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            list={listId}
            maxLength={60}
            placeholder="силуэт, детали…"
          />
          <datalist id={listId}>
            {groups.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </label>
        {single ? (
          <label className="grid gap-1">
            <span className={label}>название (необязательно)</span>
            <input
              className={`${input} w-full`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="по умолчанию — фраза"
              maxLength={120}
            />
          </label>
        ) : null}
        <p className="text-[11.5px] text-fg-faint">
          История спроса приедет после «Обновить из Wordstat». Ctrl+Enter — добавить.
        </p>
        {failed.length ? (
          <div className="whitespace-pre-wrap rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
            {`Не добавились — остались в поле:\n${failed.join("\n")}`}
          </div>
        ) : null}
        <ErrorLine error={add.error} />
      </div>
      <DialogFooter>
        <button type="button" className={btn} onClick={onCancel}>
          Отмена
        </button>
        <button type="submit" className={btnPrimary} disabled={!lines.length || add.isPending}>
          <Plus size={14} />
          {add.isPending
            ? "Добавляю…"
            : lines.length > 1
              ? `Добавить ${lines.length} ${noun(lines.length)}`
              : personal
                ? "Добавить слово"
                : "Добавить тип"}
        </button>
      </DialogFooter>
    </form>
  );
}
